import axios from 'axios';

import { MFA_METHODS } from 'constants/mfa';

/**
 * The request config that authorizes an MFA API with the short lived
 * `mfa_token` of the sign in. Without one, e.g. a signed in user adding a
 * method from the account settings, the application's access token is used.
 *
 * @method getMFATokenAuthorization
 * @param  {string}                 [mfaToken] The `mfa_token` from the authenticate response
 * @return {Object}
 */
const getMFATokenAuthorization = mfaToken =>
    mfaToken
        ? {
              headers: {
                  Authorization: `Bearer ${mfaToken}`,
                  'Content-Type': 'application/json',
              },
              ignoreAuthorization: true,
              preventRefreshToken: true,
          }
        : {};

/**
 * API call to start the enrollment of the user into an MFA method, it answers
 * with the shared secret and the `otpauth://` URI of the method.
 *
 * NOTE: the endpoint takes no request body, the method is decided by the stage
 * of the token it is authorized with. It is authorized with the short lived
 * `mfa_token` of the authenticate response instead of the application's access
 * token, and it must never go through the global refresh token flow, since the
 * API answers an unusable token with a `401`.
 *
 * @method registerMFAAPI
 * @param  {Object}        params
 * @param  {string}        [params.mfaToken] The `mfa_token` from the authenticate response,
 *                                           left out by the signed in user
 * @return {Promise}
 */
export const registerMFAAPI = ({ mfaToken } = {}) =>
    axios({
        ...getMFATokenAuthorization(mfaToken),
        method: 'POST',
        url: '/v2/mfa/register',
    });

/**
 * API call to verify the MFA code entered by the user.
 *
 * NOTE: during the sign in this request is authorized with the short lived
 * `mfa_token` instead of the application's access token. It must never go
 * through the global refresh token flow, since the API answers an invalid code
 * with a `401` that is not an expired session.
 *
 * @method verifyMFACodeAPI
 * @param  {Object}         params
 * @param  {string}         params.code     The code entered by the user
 * @param  {string}         [params.method] The MFA method being verified
 * @param  {string}         [params.mfaToken] The `mfa_token` from the authenticate response,
 *                                            left out by the signed in user
 * @return {Promise}
 */
export const verifyMFACodeAPI = ({ code, method = MFA_METHODS.TOTP, mfaToken }) =>
    axios({
        ...getMFATokenAuthorization(mfaToken),
        preventRefreshToken: true,
        data: {
            code,
            method,
        },
        method: 'POST',
        url: '/v2/mfa/verify',
    });

/**
 * API call to deactivate one or all active MFA methods of the signed in user.
 * It is authorized with the application's access token, i.e. no `mfaToken`.
 * When `type` is left out, every active method is deactivated.
 *
 * @method deactivateMFAMethodsAPI
 * @param  {Object}  params
 * @param  {string}  [params.type] The method to deactivate (`TOTP` | `EMAIL`)
 * @return {Promise}
 */
export const deactivateMFAMethodsAPI = ({ type } = {}) =>
    axios({
        method: 'DELETE',
        ...(type ? { params: { type } } : {}),
        url: '/v2/mfa/methods',
    });

/**
 * API call to reset the MFA enrollment of a user.
 *
 * @method resetUserMFAEnrollmentAPI
 * @param  {string} userId
 * @return {Promise}
 */
export const resetUserMFAEnrollmentAPI = userId =>
    axios({
        method: 'DELETE',
        type: 'auth',
        url: `/v2/mfa/users/${userId}/enrollment`,
    });
